import {Injectable} from "@angular/core";
import {ActivatedRoute, convertToParamMap, NavigationEnd, Params, Router} from "@angular/router";
import {Location} from "@angular/common";
import {filter} from "rxjs/operators";

interface LocationHistoryEntry {
  url: string;
  path?: string;
  queryParams?: {[key: string]: string};
}

interface LegacyQuery {
  query?: any;
  firstResult?: number;
  maxResults?: number;
}

interface LegacyLocationHistoryEntry {
  url: string;
  path?: string;
  queryObj?: LegacyQuery
}

@Injectable({
    providedIn: 'root'
})
export class LocationService {

  private ignoredRoutes: RegExp[] = [
    RegExp('^/Home$'),
    RegExp('^/Portal/Default$'),
    RegExp('^/Catalog/Default$'),
  ];

  private history: LocationHistoryEntry[] = [];
  private previousLocation: LocationHistoryEntry = null; // Set if we went back, so we can go forward if the dirty form check is triggered

  constructor(private router: Router, private location: Location, private activatedRoute: ActivatedRoute) {
    (window as any).NG_LOCATION_SERVICE = this;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(({urlAfterRedirects}: NavigationEnd) => {
        const ignore = this.ignoreUrl(urlAfterRedirects);
        if (!ignore) {
          if (this.getCurrentPageURL() !== urlAfterRedirects) {
            const entry = this.decodeUrlToEntry(urlAfterRedirects);
            this.history.push(entry);
          }
        }
      });
  }

  init() {
  }

  canGoBack() {
    return this.history.length > 1;
  }

  // We can only go forward 1 entry
  canGoForward() {
    return this.previousLocation != null;
  }

  back() {
    if (this.canGoBack()) {
      this.previousLocation =  this.history.pop();
      const url = this.encodeEntryToUrl(this.getCurrentPage());
      this.router.navigateByUrl(url);
    }
  }

  // We can only go forward 1 entry
  forward() {
    if (this.canGoForward()) {
      this.history.push(this.previousLocation);
      this.previousLocation = null;
      const currentLocation = this.getCurrentPage();
      const url = this.encodeEntryToUrl(currentLocation);
      return url;
    }
  }

  getCurrentPage(): LocationHistoryEntry {
    return this.history.length > 0 ? this.history[this.history.length - 1] : null;
  }

  getPreviousPage(): LocationHistoryEntry {
    return this.history.length > 1 ? this.history[this.history.length - 2] : null;
  }

  getNextPage(): LocationHistoryEntry {
    return this.canGoForward() ? this.previousLocation : null;
  }

  getLegacyCurrentPage(): LegacyLocationHistoryEntry {
    return this.history.length > 0 ? this.decodeEntryToLegacyEntry(this.history[this.history.length - 1]) : null;
  }

  getLegacyPreviousPage(): LegacyLocationHistoryEntry {
    return this.history.length > 1 ? this.decodeEntryToLegacyEntry(this.history[this.history.length - 2]) : null;
  }

  getLegacyNextPage(): LegacyLocationHistoryEntry {
    return this.canGoForward() ? this.decodeEntryToLegacyEntry(this.previousLocation) : null;
  }

  getCurrentPageURL() {
    const location = this.getCurrentPage();
    return this.encodeEntryToUrl(location);
  }

  getLegacyCurrentPageURL() {
    const location = this.getCurrentPage();
    const entry = this.decodeEntryToLegacyEntry(location);
    return this.encodeLegacyEntryToUrl(entry);
  }

  getLegacyLastPageURL() {
    const location = this.getPreviousPage();
    const entry = this.decodeEntryToLegacyEntry(location);
    return this.encodeLegacyEntryToUrl(entry);
  }

  updateCurrentParams(params: Params) {
    const currentPage = this.getCurrentPage();
    if (currentPage) {
      const currentParams = currentPage.queryParams || {};
      currentPage.queryParams = {...currentParams, ...params};
      currentPage.url = this.encodeEntryToUrl(currentPage);
    }
  }

  updateCurrentQuery(query: LegacyQuery) {
    const params: Params = {
    }
    if (query.query) {
      params.query = JSON.stringify(query.query);
    }
    if (query.firstResult != null) {
      params.firstResult = query.firstResult;
    }
    if (query.maxResults != null) {
      params.maxResults = query.maxResults;
    }

    this.updateCurrentParams(params);
  }

  remove(tableId: string) {
    this.history = this.history.filter(x => x.path.includes(tableId));
  }

  private decodeUrlToEntry(url: string): LocationHistoryEntry {

    // Use a fake base and use URL to pull the string apart, could use this.router.parseUrl(url)
    const fakeUrl = new URL(url, "https://xxx/");
    const path = fakeUrl.pathname;
    // @ts-ignore
    const queryParams = Object.fromEntries(fakeUrl.searchParams.entries());

    const entry = {
      url: url,
      path: path,
      queryParams: queryParams,
    };
    return entry;
  }

  private decodeUrlToLegacyEntry(url: string): LegacyLocationHistoryEntry {
    const entry = this.decodeUrlToEntry(url);
    return this.decodeEntryToLegacyEntry(entry);
  }

  private decodeEntryToLegacyEntry(entry: LocationHistoryEntry): LegacyLocationHistoryEntry {
    if (!entry) {
      return null;
    }

    const query = entry.queryParams.query;
    const firstResult = entry.queryParams.firstResult;
    const maxResults = entry.queryParams.maxResults;

    const queryObj: any = {
      query: query
    };
    if (firstResult != null) {
      queryObj.firstResult = Number(firstResult);
    }
    if (maxResults != null) {
      queryObj.maxResults = Number(maxResults);
    }

    const legacyEntry = {
      url: entry.url,
      path: entry.path,
      queryObj: queryObj,
    };
    return legacyEntry;
  }

  private encodeEntryToUrl(entry: LocationHistoryEntry): string {
    if (!entry) {
      return null;
    }

    const urlTree = this.router.createUrlTree([entry.path], {
      queryParams: entry.queryParams
    });
    return urlTree.toString();
  }

  private encodeLegacyEntryToUrl(entry: LegacyLocationHistoryEntry): string {
    if (!entry) {
      return null;
    }

    const queryParams: any = {
    };
    if (entry.queryObj?.query != null) {
      queryParams.query = entry.queryObj.query;
    }

    const urlTree = this.router.createUrlTree([entry.path], {
      queryParams: queryParams
    });
    return urlTree.toString();
  }

  private ignoreUrl(url: string): boolean {
    for (let re of this.ignoredRoutes) {
      if (url.match(re)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Changes the browser's URL to a normalized version of the current URL with
   * current query params merged with newQueryParams {@link Location}
   */
  mergeLocationQueryParams(newQueryParams: Params) {
      const locationPath = this.location.path(true);
      const locationUrlTree = this.router.parseUrl(locationPath);
      const params = {
          ...locationUrlTree.queryParams,
          ...newQueryParams
      };

      const urlTree = this.router.createUrlTree([], {
          queryParams: params,
          preserveFragment: true });

      this.location.go(urlTree.toString());
  }

  deleteLocationQueryParams(keys: string[]) {
    const locationPath = this.location.path(true);
    const locationUrlTree = this.router.parseUrl(locationPath);

    keys.forEach(k => {
      delete locationUrlTree.queryParams[k];
    });
    this.location.go(locationUrlTree.toString());
  }
}
